import React, { useEffect, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, Chip } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import urlJoin from 'url-join';
import { deleteOauthApplication, getOauthApplication } from '../../../services/oauth-application-service';
import { getSelectedAccount } from '../../../helpers/composer-helper';

import TlCopyToClipboard from '../../form-elements/tl-copy-to-clipboard';
import styles from '../composer.module.css';

const ApiKeysView = () => {
  const history = useHistory();

  const account = useSelector((/** @type {Object} */ state) => getSelectedAccount(state));
  const { _id: accountId } = account;

  const [oAuthApplication, setOauthApplication] = useState(null);

  const /** @type {Object<string, string>} */ { oAuthApplicationId } = useParams();

  const name = oAuthApplication && oAuthApplication.name ? oAuthApplication.name : '';

  useEffect(() => {
    if (oAuthApplicationId) {
      getOauthApplication(accountId, oAuthApplicationId).then((data) => {
        setOauthApplication((prevState) => {
          const newState = {
            ...prevState,
            ...data,
          };
          return newState;
        });
      });
    }
  }, [oAuthApplicationId]);

  /** @type {Intl.DateTimeFormatOptions} */
  const dateOpts = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  if (!oAuthApplication) {
    return null;
  }

  return (
    <div className={styles.pageCenter}>
      <Link to="/api-keys">← API Keys</Link>
      <div className={classNames(styles.flex)}>
        <h1 className={styles.h1}>{name}</h1>
        <div className={styles.f1} />
        <Button
          className={classNames(styles.secondaryButton)}
          onClick={() => {
            if (!oAuthApplication) {
              return;
            }
            if (window.confirm('Are you sure you want to delete?')) {
              deleteOauthApplication(accountId, oAuthApplicationId).then(() => {
                history.push('/api-keys');
              });
            }
          }}
        >
          Delete
        </Button>
        <Button
          component={Link}
          className={classNames(styles.keyButton)}
          to={urlJoin('/api-keys/', oAuthApplicationId, 'edit')}
        >
          Edit
        </Button>
      </div>

      <hr />
      <div className={styles.pageColumns} style={{ gap: 64 }}>
        <div style={{ flexBasis: '60%' }}>
          <div className={classNames(styles.mb6, styles.flex, styles.mt)}>
            <div>
              <div className={classNames(styles.light, styles.mb)}>Scope</div>
              <div className={styles.strong}>
                <div className={classNames(styles.strong, styles.flex)}>
                  {oAuthApplication.scopes.map((scope) => {
                    return (
                      <div key={scope}>
                        <Chip
                          className={styles.chip}
                          label={scope.replace('external-', '')}
                          style={{ marginTop: '0', height: '30px' }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className={styles.divider} />
            <div>
              <div className={classNames(styles.light, styles.mb)}>Created</div>
              <div className={styles.strong}>
                {new Date(oAuthApplication.createdAt).toLocaleDateString(undefined, dateOpts)}
              </div>
            </div>
          </div>
          <div className={styles.mb6}>
            <h2 className={styles.h2}>Credentials</h2>
            <hr />
            <div className={classNames(styles.flex, styles.mb, styles.mt2)}>
              <div className={styles.light} style={{ flexBasis: '20%' }}>
                Client ID
              </div>
              <TlCopyToClipboard
                toolTipClassOverrides={classNames(styles.pointer)}
                text={oAuthApplication.clientId}
                title="Copy Client ID"
                toolTipPlacement="right"
              >
                <div className={classNames(styles.strong, styles.inlineFlex)}>
                  {oAuthApplication.clientId} <ContentCopyRoundedIcon style={{ fontSize: 16 }} />
                </div>
              </TlCopyToClipboard>
            </div>
            <div className={classNames(styles.flex, styles.mb, styles.mt2)}>
              <div className={styles.light} style={{ flexBasis: '20%' }}>
                Client Secret
              </div>
              <TlCopyToClipboard
                toolTipClassOverrides={classNames(styles.pointer)}
                text={oAuthApplication.clientPublicSecret}
                title="Copy Client Secret"
                toolTipPlacement="right"
              >
                <div className={classNames(styles.strong, styles.inlineFlex)}>
                  {oAuthApplication.clientPublicSecret} <ContentCopyRoundedIcon style={{ fontSize: 16 }} />
                </div>
              </TlCopyToClipboard>
            </div>
          </div>
        </div>
        <div style={{ flexBasis: '40%' }} />
      </div>
    </div>
  );
};

export default ApiKeysView;
